import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Code, BookOpen, Webhook, Key } from "lucide-react";

export default function ApiDocs() {
  return (
    <div className="container mx-auto py-8 space-y-8">
      <div>
        <h1 className="text-4xl font-bold">API Documentation</h1>
        <p className="text-muted-foreground mt-2">
          Complete guide to integrating with the AthleteNexus API
        </p>
      </div>

      <Tabs defaultValue="getting-started" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="getting-started">Getting Started</TabsTrigger>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="examples">Examples</TabsTrigger>
        </TabsList>

        <TabsContent value="getting-started" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Getting Started
              </CardTitle>
              <CardDescription>
                Quick start guide to using the AthleteNexus API
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">1. Create an API Key</h3>
                <p className="text-sm text-muted-foreground">
                  Navigate to API Management and generate a new API key with the required scopes.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">2. Authentication</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Include your API key in the Authorization header:
                </p>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
{`Authorization: Bearer ak_prod_abcdef1234567890`}
                </pre>
              </div>

              <div>
                <h3 className="font-semibold mb-2">3. Base URL</h3>
                <pre className="bg-muted p-4 rounded-lg text-sm">
{`https://lpqpxlbzazvhhzzernlu.supabase.co/functions/v1`}
                </pre>
              </div>

              <div>
                <h3 className="font-semibold mb-2">4. Rate Limits</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm">Per Minute</span>
                    <Badge>100 requests</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm">Per Hour</span>
                    <Badge>5,000 requests</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="endpoints" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Deals API</CardTitle>
              <CardDescription>Manage NIL deals programmatically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">GET</Badge>
                  <code className="text-sm">/api-deals/api/v1/deals</code>
                </div>
                <p className="text-sm text-muted-foreground mb-2">List all deals</p>
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-xs font-semibold mb-2">Query Parameters:</p>
                  <ul className="text-xs space-y-1">
                    <li><code>limit</code> - Number of results (max 100)</li>
                    <li><code>offset</code> - Pagination offset</li>
                    <li><code>status</code> - Filter by status</li>
                    <li><code>athlete_id</code> - Filter by athlete</li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">GET</Badge>
                  <code className="text-sm">/api-deals/api/v1/deals/:id</code>
                </div>
                <p className="text-sm text-muted-foreground">Get deal by ID with policy violations</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary">POST</Badge>
                  <code className="text-sm">/api-deals/api/v1/deals</code>
                </div>
                <p className="text-sm text-muted-foreground mb-2">Create a new deal</p>
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-xs font-semibold mb-2">Request Body:</p>
                  <pre className="text-xs overflow-x-auto">
{`{
  "title": "Nike Endorsement",
  "athlete_id": "uuid",
  "amount": 50000,
  "brand_name": "Nike",
  "deal_type": "endorsement",
  "deliverables": ["Social posts", "Appearances"]
}`}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Athletes API</CardTitle>
              <CardDescription>Manage athlete data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">GET</Badge>
                  <code className="text-sm">/api-athletes/api/v1/athletes</code>
                </div>
                <p className="text-sm text-muted-foreground">List all athletes</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">GET</Badge>
                  <code className="text-sm">/api-athletes/api/v1/athletes/:id</code>
                </div>
                <p className="text-sm text-muted-foreground">Get athlete details with deals</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary">POST</Badge>
                  <code className="text-sm">/api-athletes/api/v1/athletes</code>
                </div>
                <p className="text-sm text-muted-foreground">Create a new athlete</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Webhook Events
              </CardTitle>
              <CardDescription>
                Subscribe to real-time events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Available Events</h3>
                <div className="space-y-2">
                  {[
                    { event: 'deal.created', desc: 'Triggered when a new deal is created' },
                    { event: 'deal.approved', desc: 'Triggered when a deal is approved' },
                    { event: 'deal.rejected', desc: 'Triggered when a deal is rejected' },
                    { event: 'deal.updated', desc: 'Triggered when a deal is updated' }
                  ].map(({ event, desc }) => (
                    <div key={event} className="p-3 bg-muted rounded">
                      <code className="text-sm font-semibold">{event}</code>
                      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Payload Format</h3>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
{`{
  "event": "deal.approved",
  "timestamp": "2025-01-27T12:00:00Z",
  "data": {
    "id": "uuid",
    "title": "Nike Endorsement",
    "amount": 50000,
    "status": "active",
    ...
  }
}`}
                </pre>
              </div>

              <div>
                <h3 className="font-semibold mb-2">HMAC Verification</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Verify webhook authenticity using the X-AthleteNexus-Signature header:
                </p>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
{`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const hmac = crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');
  
  return hmac === signature;
}`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="examples" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Code Examples
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">JavaScript / Node.js</h3>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
{`const API_KEY = 'ak_prod_your_key_here';
const BASE_URL = 'https://lpqpxlbzazvhhzzernlu.supabase.co/functions/v1';

async function getDeals() {
  const response = await fetch(\`\${BASE_URL}/api-deals/api/v1/deals\`, {
    headers: {
      'Authorization': \`Bearer \${API_KEY}\`,
      'Content-Type': 'application/json'
    }
  });
  
  return await response.json();
}

getDeals().then(data => console.log(data));`}
                </pre>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Python</h3>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
{`import requests

API_KEY = 'ak_prod_your_key_here'
BASE_URL = 'https://lpqpxlbzazvhhzzernlu.supabase.co/functions/v1'

headers = {
    'Authorization': f'Bearer {API_KEY}',
    'Content-Type': 'application/json'
}

response = requests.get(
    f'{BASE_URL}/api-deals/api/v1/deals',
    headers=headers
)

print(response.json())`}
                </pre>
              </div>

              <div>
                <h3 className="font-semibold mb-2">cURL</h3>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
{`curl -X GET \\
  https://lpqpxlbzazvhhzzernlu.supabase.co/functions/v1/api-deals/api/v1/deals \\
  -H 'Authorization: Bearer ak_prod_your_key_here' \\
  -H 'Content-Type: application/json'`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
